import React, { memo, MouseEvent, useMemo, useRef, useState } from "react";
import {
    BASE_CHART_HEIGHT,
    getActualPredictedPoints,
    getAllChartValues,
    getViewportGrid,
    iLineChartConfig, iLineChartTimePoint,
    isDashedSection as isDashedSectionHelper,
    iViewBoxProps,
} from "./LineChart.helpers";
import LineChartAxisY from "./LineChartAxisY";
import LineChartAxisX from "./LineChartAxisX";
import LineChartArea from "./LineChartArea";
import LineChartCurve, { LineStyle } from "./LineChartCurve";
import LineChartColumns from "./LineChartColumns";
import './LineChart.styles.scss';
import LineChartTooltip from "./LineChartTooltip/LineChartTooltip";
import LineChartGrid from "./LineChartGrid";
import { iTooltipColumn, iTooltipColumnsGroup, iTooltipRow } from "./LineChartTooltip/LineChartTooltip.helpers";
import useLineChartTheme from "./useLineChartTheme";
import { LineChartThemeContext } from "./LineChartTheme.context";
import { getRandom } from "@helpers";
import { FCX } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { IconMenucast } from "@icons";

const LineChart: FCX<{
    config: iLineChartConfig;
    width: number;
    height: number;
    printValue: (value: number) => string;
    printLabel: (value: number) => string;
    baseChartHeight?: number;
    isOnlyLine?: boolean;
}> = ({
    config,
    width,
    height,
    printValue,
    printLabel,
    baseChartHeight,
    isOnlyLine,
    style,
    className,
}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const [clientY, setClientY] = useState(0);
    const [activeTimeIndex, setActiveTimeIndex] = useState(-1);
    const [isVisible, setIsVisible] = useState(false);
    const { THEME } = useLineChartTheme();
    const ID = useRef(getRandom());

    // Trigger entrance animation on mount
    React.useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const baseHeight = useMemo(
        () => baseChartHeight ?? BASE_CHART_HEIGHT,
        [baseChartHeight]
    );

    const viewport = useMemo<iViewBoxProps>(() => {
        const padding = {
            top: isOnlyLine ? 0 : 15,
            bottom: isOnlyLine ? 0 : 50,
            left: isOnlyLine ? 0 : 40,
            right: isOnlyLine ? 0 : 5,
        };
        const proportion = baseHeight / Math.max(baseHeight, height);
        const scaledWidth = width * proportion;

        return {
            width: scaledWidth - padding.left - padding.right,
            height: baseHeight - padding.top - padding.bottom,
            canvasWidth: scaledWidth,
            canvasHeight: baseHeight,
            padding,
            proportion,
        };
    }, [width, height, baseHeight]);

    const y = useMemo(() => {
        if (chartRef.current) {
            return clientY - chartRef.current.getBoundingClientRect().top;
        }
        return 0;
    }, [clientY]);

    const x = useMemo(() => {
        if (activeTimeIndex === -1 || !config.series[0]) return 0;
        const timePointWidth = viewport.width / config.series[0].data.length;
        return (viewport.padding.left + timePointWidth * (activeTimeIndex + 0.5)) / viewport.proportion;
    }, [activeTimeIndex, viewport, config]);

    const { yAxisValues, getY } = useMemo(() => {
        return getViewportGrid({
            height: viewport.height,
            chartValues: getAllChartValues(config),
        });
    }, [config, viewport]);

    const yAxisData = useMemo(() => {
        return yAxisValues.map((value: number) => ({
            title: printLabel(value),
            y: getY(value),
        }));
    }, [yAxisValues, getY, printLabel]);

    const dashedPartStartX = useMemo(() => {
        if (config.dashedLineStartIndex === undefined || config.dashedLineStartIndex < 0) {
            return viewport.width;
        }
        if (!config.series[0]) return viewport.width;

        const timePointWidth = viewport.width / config.series[0].data.length;
        return timePointWidth * config.dashedLineStartIndex;
    }, [viewport, config]);

    const isSomeSeriesActive = useMemo(() => {
        return config.series.some((s: any) => s.isActive);
    }, [config.series]);

    const tooltipData: {
        rows: iTooltipRow[];
        data: iTooltipColumnsGroup[];
    } = useMemo(() => {
        const result: {
            rows: iTooltipRow[];
            data: iTooltipColumnsGroup[];
        } = {
            rows: [],
            data: [],
        };

        if (activeTimeIndex > -1 && config.showTooltip !== false) {
            const timePoint = config.series[0]?.data[activeTimeIndex];
            if (!timePoint) return result;

            const subColumns: iTooltipColumn[] = timePoint.points.map((point: any, index: number) => ({
                title: point.label,
                index,
            }));

            // Check if we have mixed actual/predicted points in sub-period data
            const hasActualPredictedMix = timePoint.points.some((p: any) => p.isActual !== undefined);

            if (hasActualPredictedMix && timePoint.points.length > 1) {
                // Split into actual and predicted based on isActual flag
                const actualPoints = subColumns.filter((_, index) => timePoint.points[index]?.isActual === true);
                const predictedPoints = subColumns.filter((_, index) => timePoint.points[index]?.isActual === false);

                if (actualPoints.length) {
                    result.data.push({
                        title: 'Actual',
                        columns: actualPoints,
                    });
                }

                if (predictedPoints.length) {
                    result.data.push({
                        title: 'Predicted',
                        columns: predictedPoints,
                    });
                }
            } else {
                result.data.push({
                    title: '',
                    columns: subColumns,
                });
            }

            // Sort series by value for tooltip display
            const sortedSeries = [...config.series].sort((a, b) => {
                const aValue = a.data[activeTimeIndex]?.points[0]?.value || 0;
                const bValue = b.data[activeTimeIndex]?.points[0]?.value || 0;
                return bValue - aValue;
            });

            result.rows = sortedSeries.map((series) => ({
                title: series.title,
                color: series.color,
                values: series.data[activeTimeIndex]?.points.map((point: any) => printValue(point.value)) || [],
            }));
        }

        return result;
    }, [activeTimeIndex, config, printValue]);

    const handleMouseMove = (event: MouseEvent) => {
        setClientY(event.clientY);
    };

    const labels = useMemo(() =>
            config.series[0]?.data.map((timePoint: any) => timePoint.label) || []
        , [config.series]);

    const currentTheme = useMemo(() => ({ THEME }), [THEME]);
    const RADIUS = 8;

    const sizes = useMemo(() => {
        return {
            height: viewport.height + viewport.padding.top + viewport.padding.bottom,
            width: viewport.width + viewport.padding.left + viewport.padding.right,
        };
    }, [viewport]);

    const hasBg = useMemo(() => !isOnlyLine, [isOnlyLine]);
    const hasXAxis = useMemo(() => !isOnlyLine, [isOnlyLine]);
    const hasYAxis = useMemo(() => !isOnlyLine, [isOnlyLine]);
    const hasGrid = useMemo(() => !isOnlyLine, [isOnlyLine]);
    const hasAreas = useMemo(() => !isOnlyLine, [isOnlyLine]);
    const hasColumns = useMemo(() => !isOnlyLine, [isOnlyLine]);
    const hasCurves = true;

    return (
        <LineChartThemeContext.Provider value={currentTheme}>
            <div
                className={cn(
                    'LineChart',
                    isVisible && 'is-visible',
                    className,
                )}
                style={{ width, height }}
                onMouseMove={handleMouseMove}
                ref={chartRef}
            >

                {activeTimeIndex >= 0 && config.showTooltip !== false && (
                    <LineChartTooltip
                        title={(
                            <>
                                {config.series[0]?.data[activeTimeIndex]?.label}
                                {config.series[0]?.data[activeTimeIndex]?.tooltipSubTitle && (
                                    <span
                                        className="LineChartTooltip__sub-title"
                                        dangerouslySetInnerHTML={{
                                            __html: `&nbsp;${config.series[0].data[activeTimeIndex].tooltipSubTitle}`,
                                        }}
                                    />
                                )}
                            </>
                        )}
                        rows={tooltipData.rows}
                        data={tooltipData.data}
                        y={y}
                        x={x}
                        position={x < width / 2 ? 'right' : 'left'}
                    />
                )}

                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={sizes.width}
                    height={sizes.height}
                    viewBox={`0 0 ${sizes.width} ${sizes.height}`}
                    style={{
                        ...style,
                        font: 'bold 12px Montserrat, sans-serif',
                    }}
                >
                    {hasBg && (
                        <g name="bg" fill={THEME.BACKGROUND.DEFAULT}>
                            <rect
                                x={0}
                                y={0}
                                width={sizes.width}
                                height={sizes.height}
                                rx={RADIUS}
                                ry={RADIUS}
                            />

                            {/* Background sections for actual vs predicted areas */}
                            <g transform={`translate(${viewport.padding.left}, ${viewport.padding.top})`}>
                                {config.series[0]?.data.map((timePoint, timeIndex) => {
                                    const timePointWidth = viewport.width / config.series[0].data.length;
                                    const isDashed = isDashedSectionHelper(timeIndex, config);

                                    if (timePoint.points.length > 1) {
                                        // Sub-period data - render individual sub-value backgrounds
                                        return timePoint.points.map((point, pointIndex) => {
                                            const pointWidth = timePointWidth / timePoint.points.length;
                                            const pointX = timePointWidth * timeIndex + pointWidth * pointIndex;
                                            const isActual = point.isActual ?? !isDashed;

                                            return (
                                                <rect
                                                    key={`bg-${timeIndex}-${pointIndex}`}
                                                    x={pointX}
                                                    y={-viewport.padding.top}
                                                    width={pointWidth}
                                                    height={viewport.height + viewport.padding.top + viewport.padding.bottom}
                                                    fill="transparent"
                                                    fillOpacity={isActual ? 0 : 0.7}
                                                />
                                            );
                                        });
                                    } else {
                                        // Single point - full width background
                                        return (
                                            <rect
                                                key={`bg-${timePoint.label}-${timeIndex}`}
                                                x={timePointWidth * timeIndex}
                                                y={-viewport.padding.top}
                                                width={timePointWidth}
                                                height={viewport.height + viewport.padding.top + viewport.padding.bottom}
                                                fill="transparent"
                                                fillOpacity={isDashed ? 0.7 : 0}
                                            />
                                        );
                                    }
                                })}
                            </g>
                        </g>
                    )}
                    <g
                        name="viewport"
                        style={{
                            width: viewport.width,
                            height: viewport.height,
                            transform: `translate(${viewport.padding.left}px, ${viewport.padding.top}px)`,
                            position: 'relative',
                        }}
                    >
                        <clipPath id={`${ID.current}-solid`}>
                            <rect
                                x={0}
                                y={0}
                                height={viewport.height + viewport.padding.top + viewport.padding.bottom}
                                width={dashedPartStartX}
                            />
                        </clipPath>
                        <clipPath id={`${ID.current}-dashed`}>
                            <rect
                                x={dashedPartStartX}
                                y={-viewport.padding.top}
                                height={viewport.height + viewport.padding.top + viewport.padding.bottom}
                                width={viewport.width - dashedPartStartX}
                            />
                        </clipPath>

                        {hasGrid && (
                            <LineChartGrid
                                viewport={viewport}
                                yAxisData={yAxisData}
                            />
                        )}
                        {hasAreas && config.series.map((series: any, index: number) => {
                            const { actualPoints, predictedPoints } = getActualPredictedPoints({
                                series,
                                viewport,
                                getY,
                            });

                            return (
                                <React.Fragment key={`area-${series.id}`}>
                                    <LineChartArea
                                        key={`area-actual-${series.id}`}
                                        points={[...actualPoints, ...predictedPoints]}
                                        color={series.color}
                                        viewport={viewport}
                                        isActive={!isSomeSeriesActive || series.isActive}
                                    />
                                </React.Fragment>
                            );
                        })}
                        {config.series.map((series: any, index: number) => {
                            const { actualPoints, predictedPoints } = getActualPredictedPoints({
                                series,
                                viewport,
                                getY,
                            });

                            return (
                                <React.Fragment key={`curve-${series.id}`}>
                                    {/* Solid line for actual data */}
                                    {actualPoints.length > 0 && (
                                        <LineChartCurve
                                            isActive={!isSomeSeriesActive || series.isActive}
                                            points={actualPoints}
                                            color={series.color}
                                            lineStyle={LineStyle.Solid}
                                        />
                                    )}
                                    {/* Dashed line for predicted data */}
                                    {predictedPoints.length > 0 && (
                                        <LineChartCurve
                                            isActive={!isSomeSeriesActive || series.isActive}
                                            points={predictedPoints}
                                            color={series.color}
                                            lineStyle={isOnlyLine ? LineStyle.Solid : LineStyle.Dashed}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {hasColumns && (
                            <LineChartColumns
                                viewport={viewport}
                                getY={getY}
                                config={config}
                                setActiveTimeIndex={setActiveTimeIndex}
                            />
                        )}
                        {hasYAxis && (
                            <LineChartAxisY
                                data={yAxisData}
                                viewport={viewport}
                            />
                        )}
                        {hasXAxis && (
                            <LineChartAxisX
                                labels={labels}
                                viewport={viewport}
                                activeTimeIndex={activeTimeIndex}
                            />
                        )}
                    </g>

                    {/* Haiku component positioned relative to last actual dot */}
                    {config.dashedLineStartIndex !== undefined && config.dashedLineStartIndex >= 0 && (() => {
                        // Find the last actual point position
                        let lastActualX = 0;

                        // Look through time periods to find last actual point
                        const timePeriodsWithSubValues = config.series[0]?.data.filter(timePoint =>
                            timePoint.points.length > 1 && timePoint.points.some(p => p.isActual !== undefined)
                        );

                        if (timePeriodsWithSubValues && timePeriodsWithSubValues.length > 0) {
                            // Get the time period with sub-values
                            const timePeriodWithSubValues = timePeriodsWithSubValues.at(-1) as iLineChartTimePoint;
                            const timePointWidth = viewport.width / config.series[0].data.length;
                            const timePeriodIndex = config.series[0].data.indexOf(timePeriodWithSubValues);
                            const pointWidth = timePointWidth / timePeriodWithSubValues.points.length;

                            // Find the last actual sub-value
                            let lastActualSubValueIndex = -1;
                            for (let i = timePeriodWithSubValues.points.length - 1; i >= 0; i--) {
                                if (timePeriodWithSubValues.points[i].isActual) {
                                    lastActualSubValueIndex = i;
                                    break;
                                }
                            }

                            if (lastActualSubValueIndex >= 0) {
                                // Calculate position of last actual sub-value dot
                                const subValueX = timePointWidth * timePeriodIndex + pointWidth * (lastActualSubValueIndex + 0.5);
                                lastActualX = subValueX + 20; // 20px offset from last actual dot
                            }
                        }

                        // Fallback to dashedPartStartX if no sub-value data found
                        if (lastActualX === 0) {
                            lastActualX = dashedPartStartX + 15;
                        }

                        return (
                            <g
                                name="haiku-logo"
                                style={{
                                    transform: `translate(${viewport.padding.left + lastActualX}px, ${viewport.height + viewport.padding.top - 65}px)`
                                }}
                            >
                                <IconMenucast
                                    className="LineChart__haiku"
                                    width={`${(3 / 15) * 100}%`}
                                    height={60}
                                />
                            </g>
                        );
                    })()}
                </svg>
            </div>
        </LineChartThemeContext.Provider>
    );
};

export default memo(LineChart);